import { collection, deleteDoc, doc, getDoc, getDocs, query, where, writeBatch, arrayRemove } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { deleteFile } from '../../firebase/storage';
import { IProject } from '../../types/Project';
import { IRecordingWithAuthor } from '../../types';
import { IPin } from '../../types/Pin';
import { PinManager } from '../PinManager';
import { RecordingManager } from '../RecordingManager';
import AlbumManager from '../AlbumManager';
import { TagManager } from '../TagManager';
import { AuthorManager } from '../AuthorManager';
import { attachAuthorsToEntities } from '../base/authorAttachment';
import { withDemoModeFallback } from '../base/demoModeUtils';
import { DemoDataManager } from '../../services/demoDataManager';
import { BatchManager } from '../base/batchUtils';
import { projectFromDoc } from './projectNormalisers';

function isDeletableStoragePath(path?: string | null): path is string {
  if (!path) return false;
  if (path.startsWith('blob:')) return false;
  if (path.startsWith('http://') || path.startsWith('https://')) return false;
  if (path.startsWith('/')) return false;
  if (path.startsWith('/assets/')) return false;
  return true;
}

function normalizeStoragePath(path: string): string {
  if (path.startsWith('gs://')) {
    const withoutScheme = path.slice('gs://'.length);
    const slashIndex = withoutScheme.indexOf('/');
    if (slashIndex >= 0) {
      return withoutScheme.slice(slashIndex + 1);
    }
  }
  return path;
}

async function deleteStorageFileSafe(path?: string | null): Promise<void> {
  if (!isDeletableStoragePath(path)) return;

  try {
    await deleteFile(normalizeStoragePath(path));
  } catch (error: unknown) {
    const code = (error as { code?: string })?.code;
    if (code === 'storage/object-not-found' || code === 'storage/unauthorized') {
      console.warn(`Storage file ${path} not deleted (${code}), skipping`);
      return;
    }
    console.warn(`Failed to delete storage file ${path}:`, error);
  }
}

async function deleteRecordingTags(
  recording: IRecordingWithAuthor,
  pinKey?: string
): Promise<void> {
  const recordingKey = recording.key;
  const effectivePinKey = pinKey || recording.pinKey;

  if (recording.author?.tags?.authorTags && effectivePinKey) {
    for (const tag of recording.author.tags.authorTags) {
      await TagManager.removePinKeyFromAuthorTag(tag, effectivePinKey);
    }
  }
  if (recording.tags?.subjectTags && effectivePinKey) {
    for (const tag of recording.tags.subjectTags) {
      await TagManager.removeRecordingKeyFromTag(tag, recordingKey, effectivePinKey);
    }
  }
  if (recording.tags?.genreTags && effectivePinKey) {
    for (const tag of recording.tags.genreTags) {
      await TagManager.removeRecordingKeyFromGenreTag(tag, recordingKey, effectivePinKey);
    }
  }
}

async function deleteAuthorIfUnused(authorKey: string): Promise<void> {
  const authorRecordings = await RecordingManager.getRecordingsByAuthorKey(authorKey);
  if (authorRecordings.length > 0) {
    return;
  }

  const author = await AuthorManager.getAuthor(authorKey);
  if (!author) {
    return;
  }

  if (
    author.image &&
    !author.image.includes('/assets/icons/user.svg') &&
    !author.image.startsWith('http') &&
    !author.image.startsWith('blob:')
  ) {
    await deleteStorageFileSafe(author.image);
  }

  await AuthorManager.deleteAuthor(authorKey);
}

export async function deleteRecordingWithAssociatedData(
  recording: IRecordingWithAuthor
): Promise<void> {
  await deleteStorageFileSafe(recording.qrPath);
  await deleteStorageFileSafe(recording.file?.recordingPath);
  await deleteStorageFileSafe(recording.file?.audioURL);
  await deleteRecordingTags(recording, recording.pinKey);

  const authorKey = recording.ownership?.recordingAuthor;
  await RecordingManager.removeRecording(recording.key);

  if (authorKey) {
    await deleteAuthorIfUnused(authorKey);
  }
}

async function removePinFromProjectTags(pin: IPin, project?: IProject | null): Promise<void> {
  if (!pin.project || !project?.tags?.subjectTag) {
    return;
  }

  for (const tagName of project.tags.subjectTag) {
    await TagManager.removeProjectTags(pin.project, tagName, pin.pinKey);
  }
}

export async function deletePinWithAssociatedData(
  pinKey: string,
  project_id: string,
  options?: {
    project?: IProject | null;
    allPins?: IPin[];
  }
): Promise<Set<string>> {
  const deletedRecordingKeys = new Set<string>();
  const pin = await PinManager.getPin(pinKey);
  if (!pin) {
    return deletedRecordingKeys;
  }

  const project = options?.project ?? (await getProjectById(project_id));
  const recordings = await RecordingManager.getRecordingsForPin(pinKey);
  const recordingsWithAuthors = await attachAuthorsToEntities(recordings);

  for (const recording of recordingsWithAuthors) {
    await deleteRecordingWithAssociatedData(recording);
    deletedRecordingKeys.add(recording.key);
  }

  const allPins = options?.allPins ?? await PinManager.getPinsByProjectKey(project_id, true);
  if (pin.pinIcon) {
    const isIconUsedElsewhere = allPins.some(
      (otherPin) => otherPin.pinKey !== pinKey && otherPin.pinIcon === pin.pinIcon
    );
    if (!isIconUsedElsewhere) {
      await deleteStorageFileSafe(pin.pinIcon);
    }
  }

  await deleteStorageFileSafe(pin.qrPath);

  if (pin.albumKey) {
    await AlbumManager.removeAlbum(pin.albumKey);
  }

  await removePinFromProjectTags(pin, project);
  await TagManager.removePinFromTags(pinKey);

  // Unlink from project.pins and delete pin atomically
  const batch = writeBatch(db);
  if (project_id) {
    batch.update(doc(db, 'Projects', project_id), {
      pins: arrayRemove(pinKey),
    });
  }
  batch.delete(doc(db, 'Pins', pinKey));
  await batch.commit();

  return deletedRecordingKeys;
}

async function deleteProjectStorageAssets(project: IProject): Promise<void> {
  await deleteStorageFileSafe(project.icon);
  await deleteStorageFileSafe(project.pinDesigns?.originalLogoUrl);
  await deleteStorageFileSafe(project.pinDesigns?.qr?.icon);
  await deleteStorageFileSafe(project.pinDesigns?.bus?.icon);
  await deleteStorageFileSafe(project.busConfig?.busIcon);

  for (const image of project.galleryImages ?? []) {
    await deleteStorageFileSafe(image.path);
  }
}

async function deleteAllProjectAccessRecords(project_id: string): Promise<void> {
  const projectAccessRef = collection(db, 'ProjectAccess');
  const accessQuery = query(projectAccessRef, where('project_id', '==', project_id));
  const snapshot = await getDocs(accessQuery);

  const batchManager = new BatchManager();
  for (const accessDoc of snapshot.docs) {
    await batchManager.addOperation((batch) => {
      batch.delete(accessDoc.ref);
    });
  }
  await batchManager.commit();
}

/**
 * Delete ProjectTags (best effort) + Project document after children are cleared.
 */
async function deleteProjectMetadata(project_id: string): Promise<void> {
  const projectTagsRef = doc(db, 'ProjectTags', project_id);
  try {
    const projectTagsSnap = await getDoc(projectTagsRef);
    if (projectTagsSnap.exists()) {
      await deleteDoc(projectTagsRef);
    }
  } catch (error) {
    console.warn(`Could not delete ProjectTags for ${project_id}:`, error);
  }

  await deleteDoc(doc(db, 'Projects', project_id));
}

async function getProjectById(project_id: string): Promise<IProject | null> {
  const projectDoc = await getDoc(doc(db, 'Projects', project_id));
  if (!projectDoc.exists()) {
    return null;
  }
  return projectFromDoc(projectDoc.id, projectDoc.data());
}

async function deleteRemainingProjectRecordings(
  project_id: string,
  deletedRecordingKeys: Set<string>
): Promise<void> {
  const projectRecordings = await RecordingManager.getRecordingsByProjectKey(project_id, true);
  const recordingsWithAuthors = await attachAuthorsToEntities(projectRecordings);

  for (const recording of recordingsWithAuthors) {
    if (deletedRecordingKeys.has(recording.key)) {
      continue;
    }

    await deleteRecordingWithAssociatedData(recording);

    if (recording.albumKey) {
      await AlbumManager.removeAlbum(recording.albumKey);
    }
  }
}

async function deleteProjectAlbums(project: IProject): Promise<void> {
  if (project.settings?.introAlbumKey) {
    await AlbumManager.removeAlbum(project.settings.introAlbumKey);
  }

  if (project.episodeAlbumKey) {
    await AlbumManager.removeAlbum(project.episodeAlbumKey);
  }
}

async function deleteProjectDocument(project_id: string): Promise<void> {
  await deleteDoc(doc(db, 'Projects', project_id));
}

async function deleteDemoProjectAndAssociatedData(project_id: string): Promise<void> {
  const pins = DemoDataManager.getPins(project_id);

  for (const pin of pins) {
    const recordings = DemoDataManager.getRecordingsForPin(pin.pinKey);
    recordings.forEach((recording) => DemoDataManager.deleteRecording(recording.key));

    if (pin.albumKey) {
      DemoDataManager.deleteAlbum(pin.albumKey);
    }

    DemoDataManager.deletePin(pin.pinKey);
  }

  const orphanedRecordings = DemoDataManager.getOrphanedRecordingsForProject(project_id);
  orphanedRecordings.forEach((recording) => DemoDataManager.deleteRecording(recording.key));

  await deleteProjectDocument(project_id);
}

async function deleteLiveProjectAndAssociatedData(project_id: string): Promise<void> {
  const project = await getProjectById(project_id);
  if (!project) {
    return;
  }

  const pins = await PinManager.getPinsByProjectKey(project_id, true);
  const deletedRecordingKeys = new Set<string>();

  for (const pin of pins) {
    const pinDeletedKeys = await deletePinWithAssociatedData(pin.pinKey, project_id, {
      project,
      allPins: pins,
    });
    pinDeletedKeys.forEach((key) => deletedRecordingKeys.add(key));
  }

  await deleteRemainingProjectRecordings(project_id, deletedRecordingKeys);
  await deleteProjectAlbums(project);
  await deleteProjectStorageAssets(project);
  await deleteAllProjectAccessRecords(project_id);
  await deleteProjectMetadata(project_id);
}

export async function deleteProjectAndAssociatedData(project_id: string): Promise<void> {
  await withDemoModeFallback(
    async () => deleteDemoProjectAndAssociatedData(project_id),
    async () => deleteLiveProjectAndAssociatedData(project_id)
  );
}

export {
  deleteStorageFileSafe,
  isDeletableStoragePath,
  normalizeStoragePath,
};
